import { apiPost } from "@/api/client";

export type WalletProviderId = "metamask" | "phantom";
export type ProviderAuthResult = { status: "authenticated" | "unlinked" };

type EthereumProvider = {
  isMetaMask?: boolean;
  providers?: EthereumProvider[];
  request: (request: {
    method: string;
    params?: unknown[];
  }) => Promise<unknown>;
};
type PhantomProvider = {
  isPhantom?: boolean;
  publicKey?: { toString: () => string };
  connect: () => Promise<{ publicKey: { toString: () => string } }>;
  signMessage: (
    message: Uint8Array,
    encoding: "utf8",
  ) => Promise<{
    signature: Uint8Array;
    publicKey?: { toString: () => string };
  }>;
};
type WalletWindow = Window & {
  ethereum?: EthereumProvider;
  phantom?: { solana?: PhantomProvider };
};

function ethereumProvider(): EthereumProvider | undefined {
  const ethereum = (window as WalletWindow).ethereum;
  return (
    ethereum?.providers?.find((provider) => provider.isMetaMask) ??
    (ethereum?.isMetaMask ? ethereum : undefined)
  );
}

function phantomProvider(): PhantomProvider | undefined {
  const phantom = (window as WalletWindow).phantom?.solana;
  return phantom?.isPhantom ? phantom : undefined;
}

export function walletAvailable(provider: WalletProviderId): boolean {
  return provider === "metamask"
    ? Boolean(ethereumProvider()?.request)
    : Boolean(phantomProvider()?.connect && phantomProvider()?.signMessage);
}

function firstEthereumAddress(value: unknown): string {
  if (
    !Array.isArray(value) ||
    typeof value[0] !== "string" ||
    !/^0x[\da-f]{40}$/i.test(value[0])
  ) {
    throw new Error("Select an Ethereum account in MetaMask and try again.");
  }
  return value[0];
}

/** Wallet APIs sign the server's exact challenge, never a client-authored message. */
export async function authenticateWallet(
  provider: WalletProviderId,
): Promise<ProviderAuthResult> {
  const ethereum = provider === "metamask" ? ethereumProvider() : undefined;
  const phantom = provider === "phantom" ? phantomProvider() : undefined;
  if (
    (!ethereum && provider === "metamask") ||
    (!phantom && provider === "phantom")
  ) {
    throw new Error(
      `Open this page in a browser with ${provider === "metamask" ? "MetaMask" : "Phantom"} installed and unlocked.`,
    );
  }

  const address = ethereum
    ? firstEthereumAddress(
        await ethereum.request({ method: "eth_requestAccounts" }),
      )
    : (await phantom!.connect()).publicKey.toString();
  if (!address || address.length > 128)
    throw new Error("The wallet did not provide a valid account.");
  const challenge = await apiPost<{
    challengeId: string;
    message: string;
    address?: string;
    provider?: string;
  }>("/session/wallet/challenge", { provider, address, purpose: "login" });
  if (
    !challenge ||
    typeof challenge.challengeId !== "string" ||
    !challenge.challengeId ||
    typeof challenge.message !== "string" ||
    !challenge.message ||
    challenge.message.length > 8192 ||
    (challenge.address !== undefined &&
      (provider === "metamask"
        ? challenge.address.toLowerCase() !== address.toLowerCase()
        : challenge.address !== address)) ||
    (challenge.provider !== undefined && challenge.provider !== provider)
  ) {
    throw new Error(
      "The wallet challenge could not be verified. Please try again.",
    );
  }

  const bytes = new TextEncoder().encode(challenge.message);
  let signature: string;
  if (ethereum) {
    const messageHex = `0x${Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("")}`;
    const signed = await ethereum.request({
      method: "personal_sign",
      params: [messageHex, address],
    });
    if (typeof signed !== "string" || !/^0x[\da-f]{130}$/i.test(signed)) {
      throw new Error("MetaMask did not return a valid signature.");
    }
    const currentAddress = firstEthereumAddress(
      await ethereum.request({ method: "eth_accounts" }),
    );
    if (currentAddress.toLowerCase() !== address.toLowerCase()) {
      throw new Error("Your wallet account changed. Please try again.");
    }
    signature = signed;
  } else {
    const signed = await phantom!.signMessage(bytes, "utf8");
    if (
      !(signed.signature instanceof Uint8Array) ||
      signed.signature.length !== 64 ||
      phantom!.publicKey?.toString() !== address ||
      (signed.publicKey && signed.publicKey.toString() !== address)
    ) {
      throw new Error(
        "Your wallet account changed or the signature was invalid. Please try again.",
      );
    }
    // The operator's Solana proof contract uses canonical base58, not base64.
    const alphabet =
      "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";
    let value = BigInt(
      `0x${Array.from(signed.signature, (byte) => byte.toString(16).padStart(2, "0")).join("")}`,
    );
    signature = "";
    while (value > 0n) {
      signature = alphabet[Number(value % 58n)] + signature;
      value /= 58n;
    }
    for (const byte of signed.signature) {
      if (byte !== 0) break;
      signature = `1${signature}`;
    }
  }

  const result = await apiPost<{
    authenticated?: boolean;
    requiresSignIn?: boolean;
    pendingLink?: boolean;
  }>("/session/wallet/authenticate", {
    provider,
    address,
    purpose: "login",
    challengeId: challenge.challengeId,
    signature,
  });
  if (
    result.authenticated === false &&
    result.requiresSignIn === true &&
    result.pendingLink === true
  )
    return { status: "unlinked" };
  if (result.authenticated === true) return { status: "authenticated" };
  throw new Error("Wallet sign-in did not establish a session.");
}
